// CallsPage — EVE's autonome Trade-Call-History.
// Datenquelle: useTradingLog.closedTrades (analog Trading-Tab Closed-Trades).
// Filter/Sort/Search Wrapper, dann Re-Use der EveClosedTradesTable.

import { useMemo, useState } from 'react';
import { History } from 'lucide-react';
import { useTradingLog } from '@/hooks/use-trading-log';
import { useEvePortfolio } from '@/hooks/use-eve-portfolio';
import { EveClosedTradesTable } from '@/components/trading/eve-closed-trades-table';

type RangeKey = '7d' | '30d' | '90d' | 'all';

const RANGE_DAYS: Record<RangeKey, number | null> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  all: null,
};

function fmtBig(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${n >= 0 ? '' : '−'}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${n >= 0 ? '' : '−'}$${(abs / 1_000).toFixed(1)}K`;
  return `${n >= 0 ? '' : '−'}$${abs.toFixed(0)}`;
}

export function CallsPage() {
  const { closedTrades } = useTradingLog();
  const { portfolio } = useEvePortfolio();
  const [range, setRange] = useState<RangeKey>('30d');

  const filtered = useMemo(() => {
    const days = RANGE_DAYS[range];
    if (days === null) return closedTrades;
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
    return closedTrades.filter((t) => {
      if (!t.closedAt) return false;
      const ts = Date.parse(t.closedAt);
      return Number.isFinite(ts) && ts >= cutoff;
    });
  }, [closedTrades, range]);

  const totals = useMemo(() => {
    let pnl = 0;
    let wins = 0;
    let losses = 0;
    for (const t of filtered) {
      const p = t.pnlAbsolute ?? 0;
      pnl += p;
      if (p > 0) wins++;
      else if (p < 0) losses++;
    }
    const total = wins + losses;
    const winRate = total > 0 ? (wins / total) * 100 : 0;
    const roi = portfolio.startCapital > 0 ? (pnl / portfolio.startCapital) * 100 : 0;
    return { pnl, wins, losses, total: filtered.length, winRate, roi };
  }, [filtered, portfolio.startCapital]);

  return (
    <div className="h-full overflow-y-auto p-5">
      <div className="mx-auto max-w-6xl space-y-4">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 pb-4">
          <div className="flex items-center gap-3">
            <History className="size-5 text-amber-400" />
            <div>
              <h1 className="text-xl font-bold">EVE Calls</h1>
              <p className="mt-0.5 text-[12px] text-neutral-500">
                Vollständige Historie aller von EVE autonom geschlossenen Trades.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {(['7d', '30d', '90d', 'all'] as const).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRange(r)}
                className={
                  range === r
                    ? 'rounded-lg border border-amber-500/30 bg-amber-500/15 px-3 py-1.5 text-[12px] font-bold text-amber-300'
                    : 'rounded-lg border border-white/10 px-3 py-1.5 text-[12px] text-neutral-400 hover:bg-white/5'
                }
              >
                {r === 'all' ? 'Alle' : r}
              </button>
            ))}
          </div>
        </header>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div className="glass-strong rounded-xl p-3">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Calls</div>
            <div className="mt-1 text-base font-bold tabular">{totals.total}</div>
          </div>
          <div className="glass-strong rounded-xl p-3">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">PnL gesamt</div>
            <div className={`mt-1 text-base font-bold tabular ${totals.pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {fmtBig(totals.pnl)}
            </div>
          </div>
          <div className="glass-strong rounded-xl p-3">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Win-Rate</div>
            <div className="mt-1 text-base font-bold tabular">
              {totals.total === 0 ? '—' : `${totals.winRate.toFixed(0)} %`}
            </div>
            <div className="mt-0.5 text-[10px] text-neutral-500">
              {totals.wins}W · {totals.losses}L
            </div>
          </div>
          <div className="glass-strong rounded-xl p-3">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">ROI vs Start</div>
            <div className={`mt-1 text-base font-bold tabular ${totals.roi >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {totals.roi >= 0 ? '+' : ''}
              {totals.roi.toFixed(2)} %
            </div>
          </div>
        </div>

        <EveClosedTradesTable trades={filtered} />
      </div>
    </div>
  );
}
